import { useEffect, useState, startTransition } from "react";
import { HUMAN_ALLOWED_TRANSITIONS, type PipelineStage } from "@newark/desk-core";
import { api, type QueueItem } from "./api";

const STATES = ["", "TX", "OK", "AR", "CO", "NM", "HI", "AK", "SD", "WI", "CA", "FL"];
const LABEL_FORMATS = ["avery-5160", "avery-5163", "dymo-30252", "dymo-30321", "csv"] as const;

function stageOptions(current: PipelineStage): PipelineStage[] {
  const next = HUMAN_ALLOWED_TRANSITIONS[current] ?? [];
  return [current, ...next.filter((s) => s !== current)];
}

function stageBadge(stage: PipelineStage): string {
  switch (stage) {
    case "new":
      return "bg-[color:var(--color-pine)]/12 text-[color:var(--color-pine)]";
    case "review":
    case "pursue":
      return "bg-[color:var(--color-clay)]/15 text-[color:var(--color-clay)]";
    case "drafted":
    case "approved":
      return "bg-[color:var(--color-ink)]/8 text-[color:var(--color-ink)]";
    case "mailed":
    case "retained":
      return "bg-[color:var(--color-ok)]/15 text-[color:var(--color-ok)]";
    case "closed":
      return "bg-black/8 text-[color:var(--color-ink-soft)]";
    default:
      return "bg-black/5";
  }
}

export function Queue({ onLogout }: { onLogout: () => void }) {
  const [showClosed, setShowClosed] = useState(false);
  const [state, setState] = useState("");
  const [items, setItems] = useState<QueueItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState("");
  const [format, setFormat] = useState<(typeof LABEL_FORMATS)[number]>("avery-5160");
  const [batchMsg, setBatchMsg] = useState<string | null>(null);
  const [batches, setBatches] = useState<
    Array<{ id: string; status: string; item_count: number; format: string; created_at: string }>
  >([]);

  function reload() {
    setLoading(true);
    setError(null);
    api
      .queue(showClosed, state || undefined)
      .then((data) => {
        startTransition(() => {
          setItems(data.results);
          setSource(data.source);
        });
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Load failed"))
      .finally(() => setLoading(false));
    api
      .listBatches()
      .then((b) => setBatches(b.results))
      .catch(() => undefined);
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showClosed, state]);

  async function changeStage(item: QueueItem, stage: PipelineStage) {
    try {
      const row = await api.setStage(String(item.id), stage);
      setItems((prev) =>
        prev.map((it) =>
          String(it.id) === String(item.id)
            ? { ...it, workup: { ...(it.workup ?? row), ...row } }
            : it,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Stage update failed");
    }
  }

  async function saveAddress(item: QueueItem) {
    const name = window.prompt("Recipient name", item.workup?.mail_name ?? item.borrower_name ?? "CURRENT RESIDENT");
    if (name == null) return;
    const address = window.prompt(
      "Mail address (street\\ncity ST ZIP)",
      item.workup?.mail_address ??
        [item.property_address, item.city && item.state && item.zip ? `${item.city} ${item.state} ${item.zip}` : null]
          .filter(Boolean)
          .join("\n"),
    );
    if (address == null) return;
    const verified = window.confirm("Mark address verified?");
    try {
      const row = await api.saveMail(String(item.id), name, address, verified);
      setItems((prev) =>
        prev.map((it) =>
          String(it.id) === String(item.id)
            ? { ...it, workup: row, hasMailAddress: true }
            : it,
        ),
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function createBatch() {
    const ids = selected.size
      ? [...selected]
      : items.filter((i) => i.hasMailAddress || i.workup?.address_verified).map((i) => String(i.id));
    if (!ids.length) {
      setBatchMsg("Select notices with verified mail addresses first.");
      return;
    }
    setBatchMsg("Creating batch…");
    try {
      const res = await api.createLabelBatch(ids, format, "ADVERTISING MATERIAL");
      setBatchMsg(
        `Batch ${res.batch.id}: ${res.included} labels, ${res.skips.length} skipped (unverified/incomplete/DNC/dedupe).`,
      );
      setSelected(new Set());
      reload();
    } catch (err) {
      setBatchMsg(err instanceof Error ? err.message : "Batch failed");
    }
  }

  async function logout() {
    await api.logout().catch(() => undefined);
    onLogout();
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
      <header className="animate-fade-up flex flex-wrap items-end justify-between gap-4 border-b border-[color:var(--color-pine)]/20 pb-5">
        <div>
          <p className="text-xs font-medium tracking-[0.2em] text-[color:var(--color-pine)] uppercase">
            Staging · No letters
          </p>
          <h1 className="mt-1 font-[family-name:var(--font-display)] text-3xl text-[color:var(--color-ink)] sm:text-4xl">
            Newark Law Offices
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-soft)]">
            Foreclosure queue · mailing labels · pipeline stages
            {source ? ` · source ${source}` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={logout}
          className="rounded-md border border-[color:var(--color-ink)]/15 bg-white/60 px-3 py-1.5 text-sm"
        >
          Sign out
        </button>
      </header>

      <div className="animate-fade-up-delay mt-5 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
          />
          Show closed
        </label>
        <select
          value={state}
          onChange={(e) => setState(e.target.value)}
          className="rounded-md border border-[color:var(--color-ink)]/15 bg-white/70 px-2 py-1.5 text-sm"
        >
          {STATES.map((s) => (
            <option key={s || "all"} value={s}>
              {s || "All states"}
            </option>
          ))}
        </select>
        <select
          value={format}
          onChange={(e) => setFormat(e.target.value as (typeof LABEL_FORMATS)[number])}
          className="rounded-md border border-[color:var(--color-ink)]/15 bg-white/70 px-2 py-1.5 text-sm"
        >
          {LABEL_FORMATS.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={createBatch}
          className="rounded-md bg-[color:var(--color-pine)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[color:var(--color-pine-deep)]"
        >
          Create label batch{selected.size ? ` (${selected.size})` : ""}
        </button>
        {batchMsg ? <span className="text-sm text-[color:var(--color-ink-soft)]">{batchMsg}</span> : null}
      </div>

      {error ? (
        <p className="mt-4 text-sm text-[color:var(--color-danger)]">{error}</p>
      ) : null}

      {loading ? (
        <p className="mt-8 animate-pulse-soft text-sm text-[color:var(--color-ink-soft)]">Loading queue…</p>
      ) : (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-[color:var(--color-pine)]/20 text-xs tracking-wide text-[color:var(--color-moss)] uppercase">
                <th className="px-2 py-2 w-8" />
                <th className="px-2 py-2">Property</th>
                <th className="px-2 py-2">Sale</th>
                <th className="px-2 py-2">Score</th>
                <th className="px-2 py-2">Stage</th>
                <th className="px-2 py-2">Mail</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const stage = (item.workup?.stage ?? "new") as PipelineStage;
                const id = String(item.id);
                return (
                  <tr
                    key={id}
                    className="border-b border-[color:var(--color-ink)]/8 align-top transition-colors hover:bg-white/40"
                  >
                    <td className="px-2 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(id)}
                        onChange={() => toggleSelect(id)}
                      />
                    </td>
                    <td className="px-2 py-3">
                      <div className="font-medium text-[color:var(--color-ink)]">
                        {item.property_address ?? "Address not listed"}
                      </div>
                      <div className="mt-0.5 text-xs text-[color:var(--color-ink-soft)]">
                        {[item.county, item.state, item.lender_servicer].filter(Boolean).join(" · ")}
                      </div>
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap">{item.sale_date ?? "—"}</td>
                    <td className="px-2 py-3 font-medium">{item.score}</td>
                    <td className="px-2 py-3">
                      <span className={`inline-block rounded px-2 py-0.5 text-xs ${stageBadge(stage)}`}>
                        {stage}
                      </span>
                      <select
                        className="mt-1 block w-full max-w-[9rem] rounded border border-[color:var(--color-ink)]/12 bg-white/70 px-1 py-1 text-xs"
                        value={stage}
                        onChange={(e) => changeStage(item, e.target.value as PipelineStage)}
                      >
                        {stageOptions(stage).map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-3">
                      <button
                        type="button"
                        onClick={() => saveAddress(item)}
                        className="text-xs text-[color:var(--color-pine)] underline-offset-2 hover:underline"
                      >
                        {item.workup?.address_verified
                          ? "Verified address"
                          : item.hasMailAddress
                            ? "Edit address"
                            : "Add address"}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!items.length ? (
                <tr>
                  <td colSpan={6} className="px-2 py-10 text-center text-[color:var(--color-ink-soft)]">
                    No notices{showClosed ? "" : " (closed hidden)"}.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      {batches.length ? (
        <section className="mt-10 border-t border-[color:var(--color-pine)]/20 pt-6">
          <h2 className="font-[family-name:var(--font-display)] text-xl">Label batches</h2>
          <p className="mt-1 text-sm text-[color:var(--color-ink-soft)]">
            Approve → print → mail are human-only. Download with{" "}
            <code className="text-xs">?format=avery-5160|avery-5163|dymo-30252|dymo-30321|csv</code>
            {" "}or <code className="text-xs">?align=1</code> for alignment test.
          </p>
          <ul className="mt-4 space-y-2">
            {batches.map((b) => (
              <li
                key={b.id}
                className="flex flex-wrap items-center gap-2 rounded-md bg-white/50 px-3 py-2 text-sm"
              >
                <span className="font-mono text-xs">{b.id}</span>
                <span>
                  {b.status} · {b.item_count} · {b.format}
                </span>
                <a
                  className="text-[color:var(--color-pine)] underline"
                  href={`/api/labels/batch/${b.id}?format=${b.format}`}
                >
                  Download
                </a>
                <a
                  className="text-[color:var(--color-pine)] underline"
                  href={`/api/labels/batch/${b.id}?format=${b.format}&align=1`}
                >
                  Align test
                </a>
                {b.status === "draft" ? (
                  <button type="button" className="underline" onClick={() => api.approveBatch(b.id).then(reload)}>
                    Approve
                  </button>
                ) : null}
                {b.status === "approved" ? (
                  <button type="button" className="underline" onClick={() => api.printBatch(b.id).then(reload)}>
                    Mark printed
                  </button>
                ) : null}
                {b.status === "printed" ? (
                  <button type="button" className="underline" onClick={() => api.mailBatch(b.id).then(reload)}>
                    Mark mailed
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
